import { useEffect, useRef, useState } from "react";

interface SplitFlapValueProps {
  text: string;
  ariaLabel: string;
}

/**
 * Signature UI element: renders `text` as individual monospace character tiles,
 * reminiscent of a split-flap (Solari) departure board. When the text changes
 * (new series or new latest value), each tile plays a brief staggered flip.
 * Respects prefers-reduced-motion by cross-fading instead (handled purely in CSS).
 */
export function SplitFlapValue({ text, ariaLabel }: SplitFlapValueProps) {
  const [generation, setGeneration] = useState(0);
  const previous = useRef(text);

  useEffect(() => {
    if (previous.current !== text) {
      previous.current = text;
      setGeneration((g) => g + 1);
    }
  }, [text]);

  const characters = Array.from(text);

  return (
    <span className="rk-split-flap" role="text" aria-label={ariaLabel}>
      {characters.map((char, i) => (
        <span
          key={`${generation}-${i}`}
          className="rk-flap-tile"
          style={{ animationDelay: `${i * 35}ms` }}
          aria-hidden="true"
        >
          {char === " " ? " " : char}
        </span>
      ))}
    </span>
  );
}
